import React, { useState, useEffect } from 'react';
import { tokens } from '../theme';

const API = 'https://tcio-api.onrender.com';

export default function LeadIntakeForm({ user, onCreated, onCancel }) {
  const [lk, setLk] = useState(null);
  const [f, setF] = useState({
    first_name:'', last_name:'', phone:'', email:'', dob:'', gender:'', patient_state:'',
    caller_name:'', caller_relationship:'', caller_phone:'',
    payer_name:'', plan_name:'', member_id:'', group_number:'', rx_bin:'', rx_pcn:'',
    in_network:'', deductible:'', deductible_met:'', oop_max:'', oop_met:'',
    oon_coverage_pct:'', auth_required:'', mental_health_benefit:'',
    auth_number:'', auth_thru:'',
    primary_substance:'', last_use:'', detox_risk:'', prior_treatment:'',
    mh_diagnosis:'', si_hi:'',
    facility_id:'',
    loc_requested:'', payment_type:'insurance', how_soon:'', needs_transport:'',
    lead_source_id:'', referral_partner_id:'',
    therapist_id:'', case_manager_id:'', coordinator_id:'',
    assigned_to:'', notes:'',
  });
  const [busy, setBusy] = useState(false);
  const [score, setScore] = useState(null);

  const headers = { Authorization: `Bearer ${user.token}` };
  const jh = { ...headers, 'Content-Type':'application/json' };

  useEffect(() => {
    fetch(`${API}/api/lookups`, { headers })
      .then(r => r.json()).then(j => {
        if (j.ok) {
          setLk(j);
          setF(p => ({ ...p, assigned_to: user.id, coordinator_id: user.id }));
        }
      }).catch(() => {});
  }, []);

  // payer intelligence preview
  useEffect(() => {
    if (!f.payer_name) return setScore(null);
    fetch(`${API}/api/payer-score/${encodeURIComponent(f.payer_name)}`, { headers })
      .then(r => r.json())
      .then(j => setScore(j.found ? j : null))
      .catch(() => setScore(null));
  }, [f.payer_name]);

  const set = (k,v) => setF(p => ({ ...p, [k]: v }));

  const canVob = f.first_name && f.last_name && /^\d{2}\/\d{2}\/\d{4}$/.test(f.dob || '') && f.payer_name && f.member_id;
  const vobMissing = () => {
    const miss = [];
    if (!f.first_name) miss.push('First name');
    if (!f.last_name) miss.push('Last name');
    if (!f.dob) miss.push('DOB');
    else if (!/^\d{2}\/\d{2}\/\d{4}$/.test(f.dob)) miss.push('DOB (needs full MM/DD/YYYY — currently "' + f.dob + '")');
    if (!f.payer_name) miss.push('Payer (pick from the dropdown)');
    if (!f.member_id) miss.push('Member ID');
    return miss;
  };

  const create = async (thenVob) => {
    if (!f.first_name || !f.last_name || !f.phone) {
      return alert('First name, last name, and phone are required.');
    }
    if (!f.facility_id) {
      return alert('Select a facility. Every lead must belong to a facility.');
    }
    setBusy(true);
    try {
      const submit = (extra) => fetch(`${API}/api/leads/v2`, {
        method:'POST', headers: jh, body: JSON.stringify({ ...f, dob: dobToISO(f.dob), ...(extra || {}) })
      });
      let r = await submit();
      let j = await r.json();

      // Duplicate / readmit guard (server returns 409 + matches when this person already exists)
      if (r.status === 409 && j.duplicate_check && j.matches && j.matches.length) {
        const m = j.matches[0];
        const lines = [];
        if (m.do_not_admit) lines.push('*** DO NOT ADMIT flag on this person' + (m.dna_reason ? ': ' + m.dna_reason : '') + ' ***');
        lines.push('A matching person already exists: ' + m.first_name + ' ' + m.last_name + (m.dob ? ' (DOB ' + String(m.dob).slice(0,10) + ')' : ''));
        if (m.open_lead_id) lines.push('ACTIVE LEAD - stage ' + (m.pipeline_stage || '?') + ', created ' + String(m.lead_created_at).slice(0,10) + (m.rep_name ? ', assigned to ' + m.rep_name : ''));
        if (m.patient_id) lines.push('FORMER CLIENT - ' + (m.discharge_date ? 'discharged ' + String(m.discharge_date).slice(0,10) : 'admitted ' + String(m.last_admit_date).slice(0,10)) + (m.facility_name ? ' at ' + m.facility_name : ''));
        const story = lines.join('\n');

        if (m.open_lead_id && window.confirm(story + '\n\nOPEN the existing lead instead of creating a duplicate?')) {
          window.location.hash = '#/lead/' + m.open_lead_id;
          setBusy(false);
          onCreated && onCreated(m.open_lead_id);
          return;
        }
        if (m.patient_id && window.confirm(story + '\n\nCreate as a READMIT? The same client record is reused and the lead source is set to Alumni / Previous Patient.')) {
          r = await submit({ duplicate_override: 'readmit', readmit_contact_id: m.contact_id });
          j = await r.json();
        } else if (window.confirm(story + '\n\nCreate a brand-new lead anyway?')) {
          r = await submit({ duplicate_override: true });
          j = await r.json();
        } else {
          setBusy(false);
          return;
        }
      }

      if (!j.ok) { alert('Error: ' + j.error); setBusy(false); return; }

      if (thenVob) {
        // IN-HOUSE VOB (Jaclyn, ticket 481ede42, 11/2): same as the lead page — a facility that verifies in-house sends the request to the
        // VOB work queue and runs the electronic check; only a facility on an outside biller falls through to the email request below.
        const f0r = await fetch(`${API}/api/vob/queue/${j.lead_id}/lead-request`, { method:'POST', headers: jh, body: JSON.stringify({}) }).catch(() => null);
        const f0 = f0r ? await f0r.json().catch(() => ({})) : {};
        if (f0.mode === 'in_house') {
          if (!f0.ok) { alert('Lead created, but the VOB was not requested: ' + (f0.error || 'unknown error')); setBusy(false); onCreated && onCreated(j.lead_id); return; }
          let line = 'Already on file.';
          if (!f0.already) { let cj = {}; try { const cr = await fetch(`${API}/api/vob/workspace/${j.lead_id}/check`, { method:'POST', headers: jh, body: JSON.stringify({}) }); cj = await cr.json().catch(() => ({})); } catch (e) { cj = {}; }
            line = cj.ok ? cj.message : cj.repeat ? 'It was already run a few minutes ago.' : (cj.error || 'The electronic check did not run; the VOB team has it in their queue.'); }
          alert('Lead created.\nVOB sent to the in-house VOB queue. ' + line);
          setBusy(false); onCreated && onCreated(j.lead_id); return;
        }
        const v = await fetch(`${API}/api/leads/v2/${j.lead_id}/request-vob`, {
          method:'POST', headers: jh, body: JSON.stringify({})
        });
        let vj = await v.json();
        if (vj.duplicate) {
          const p = vj.prior;
          const when = new Date(p.requested_at).toLocaleDateString('en-US',{month:'short',day:'numeric'});
          if (window.confirm(`A VOB was already requested for this client on ${when} by ${p.by} (ref ${p.ref_code}, status: ${p.status}).\n\nSend it AGAIN anyway?`)) {
            const v2 = await fetch(`${API}/api/leads/v2/${j.lead_id}/request-vob`, {
              method:'POST', headers: jh, body: JSON.stringify({ force: true })
            });
            vj = await v2.json();
          } else vj = { ok: false, error: 'duplicate — not re-sent' };
        }
        if (vj.ok) alert(`Lead created.\nVOB requested — Ref ${vj.ref_code}`);
        else alert('Lead created, but VOB failed: ' + vj.error);
      }

      onCreated && onCreated(j.lead_id);
    } catch (e) { alert(e.message); }
    setBusy(false);
  };

  const addPartner = async () => {
    const name = prompt('New referral partner:');
    if (!name) return;
    const r = await fetch(`${API}/api/referral-partners`, {
      method:'POST', headers: jh, body: JSON.stringify({ name })
    });
    const j = await r.json();
    if (j.ok) {
      setLk(await (await fetch(`${API}/api/lookups`, { headers })).json());
      set('referral_partner_id', j.data.id);
    }
  };

  const addStaff = async (type) => {
    const first = prompt(`New ${type.replace('_',' ')} — first name:`);
    if (!first) return;
    const last = prompt('Last name (optional):');
    const r = await fetch(`${API}/api/staff`, {
      method:'POST', headers: jh,
      body: JSON.stringify({ first_name:first, last_name:last||null, staff_type:type })
    });
    const j = await r.json();
    if (j.ok) {
      setLk(await (await fetch(`${API}/api/lookups`, { headers })).json());
      set(type === 'therapist' ? 'therapist_id' : 'case_manager_id', j.data.id);
    }
  };

  if (!lk) return <div style={s.wrap}><div style={s.empty}>Loading…</div></div>;

  const nm = (x) => `${x.first_name} ${x.last_name || ''}`.trim();

  return (
    <div style={s.wrap}>
      <h1 style={s.h1}>New Lead</h1>
      <p style={s.sub}>Fill in what you have. You can add the rest later.</p>

      <div style={s.card}>
        <Sec t="Patient" />
        <Grid>
          <F l="First Name *" v={f.first_name} on={v=>set('first_name',v)} />
          <F l="Last Name *"  v={f.last_name}  on={v=>set('last_name',v)} />
          <F l="Phone *"      v={f.phone}      on={v=>set('phone',v)} ph="(555) 555-5555" />
          <F l="Email"        v={f.email}      on={v=>set('email',v)} />
          <F l="DOB" v={f.dob} on={v=>set('dob', maskDob(v))} ph="MM/DD/YYYY" />
          <F l="Gender" v={f.gender} on={v=>set('gender',v)} opts={['','male','female','other']} />
          <F l="State"  v={f.patient_state} on={v=>set('patient_state',v)} />
        </Grid>

        <Sec t="Caller (if not the patient)" />
        <Grid>
          <F l="Caller Name" v={f.caller_name} on={v=>set('caller_name',v)} />
          <F l="Relationship" v={f.caller_relationship} on={v=>set('caller_relationship',v)}
             opts={['', ...lk.relationships]} />
          <F l="Caller Phone" v={f.caller_phone} on={v=>set('caller_phone',v)} />
        </Grid>

        <Sec t="Insurance" />
        <Grid>
          <F l="Payer" v={f.payer_name} on={v=>set('payer_name',v)} opts={[...lk.payers].sort()} wide search ph="Type to search payers…" />
          <F l="Plan Name" v={f.plan_name} on={v=>set('plan_name',v)} />
          <F l="Member ID" v={f.member_id} on={v=>set('member_id',v)} />
          <F l="Group #"   v={f.group_number} on={v=>set('group_number',v)} />
          <F l="RX BIN"    v={f.rx_bin} on={v=>set('rx_bin',v)} />
          <F l="RX PCN"    v={f.rx_pcn} on={v=>set('rx_pcn',v)} />
          <F l="In Network?" v={f.in_network} on={v=>set('in_network',v)} opts={YN} />
          <F l="Deductible" type="number" v={f.deductible} on={v=>set('deductible',v)} />
          <F l="Deductible Met" type="number" v={f.deductible_met} on={v=>set('deductible_met',v)} />
          <F l="OOP Max" type="number" v={f.oop_max} on={v=>set('oop_max',v)} />
          <F l="OOP Met" type="number" v={f.oop_met} on={v=>set('oop_met',v)} />
          <F l="OON %"   type="number" v={f.oon_coverage_pct} on={v=>set('oon_coverage_pct',v)} />
          <F l="Auth Required" v={f.auth_required} on={v=>set('auth_required',v)} opts={YN} />
          <F l="MH Benefit" v={f.mental_health_benefit} on={v=>set('mental_health_benefit',v)} opts={YN} />
          <F l="Auth #" v={f.auth_number} on={v=>set('auth_number',v)} />
          <F l="Auth Thru" type="date" v={f.auth_thru} on={v=>set('auth_thru',v)} />
        </Grid>

        {score && <PayerCard score={score} />}

        <Sec t="Clinical" />
        <Grid>
          <F l="Primary Substance" v={f.primary_substance} on={v=>set('primary_substance',v)}
             opts={['', ...lk.substances]} />
          <F l="Last Use" v={f.last_use} on={v=>set('last_use',v)} ph="e.g. this morning" />
          <F l="Detox Risk" v={f.detox_risk} on={v=>set('detox_risk',v)} opts={['', ...lk.detox_risk]} />
          <F l="Prior Treatment" v={f.prior_treatment} on={v=>set('prior_treatment',v)} opts={YN} />
          <F l="SI / HI" v={f.si_hi} on={v=>set('si_hi',v)} opts={YN} />
          <F l="MH Diagnosis" v={f.mh_diagnosis} on={v=>set('mh_diagnosis',v)} wide />
        </Grid>

        <Sec t="Logistics" />
        <Grid>
          <F l="Facility" v={f.facility_id} on={v=>set('facility_id',v)}
             opts={[['','—'], ...lk.facilities.map(x=>[x.id,x.org_name])]} />
          <F l="LOC" v={f.loc_requested} on={v=>set('loc_requested',v)} opts={['', ...lk.locs]} />
          <F l="Payment Type" v={f.payment_type} on={v=>set('payment_type',v)} opts={['insurance', 'scholarship', 'cash', 'single_case_agreement']} />
          <F l="How Soon" v={f.how_soon} on={v=>set('how_soon',v)} opts={['', ...lk.how_soon]} />
          <F l="Needs Transport" v={f.needs_transport} on={v=>set('needs_transport',v)} opts={YN} />
        </Grid>

        <Sec t="Attribution" />
        <Grid>
          <F l="Lead Source" v={f.lead_source_id} on={v=>set('lead_source_id',v)}
             opts={[['','—'], ...lk.lead_sources.map(x=>[x.id,x.name])]} />
          <F l="Referral Partner" v={f.referral_partner_id} on={v=>set('referral_partner_id',v)}
             opts={[['','—'], ...lk.referral_partners.map(x=>[x.id,x.name])]} onAdd={addPartner} />
          <F l="Assigned Rep" v={f.assigned_to} on={v=>set('assigned_to',v)}
             opts={[['','—'], ...lk.reps.map(x=>[x.id,`${x.first_name} ${x.last_name}`])]} />
        </Grid>

        <Sec t="Clinical Team (carries to Bed Board)" />
        <Grid>
          <F l="Therapist" v={f.therapist_id} on={v=>set('therapist_id',v)}
             opts={[['','—'], ...lk.therapists.map(x=>[x.id, nm(x)])]}
             onAdd={() => addStaff('therapist')} />
          <F l="Case Manager" v={f.case_manager_id} on={v=>set('case_manager_id',v)}
             opts={[['','—'], ...lk.case_managers.map(x=>[x.id, nm(x)])]}
             onAdd={() => addStaff('case_manager')} />
          <F l="Coordinator" v={f.coordinator_id} on={v=>set('coordinator_id',v)}
             opts={[['','—'], ...lk.coordinators.map(x=>[x.id,`${x.first_name} ${x.last_name}`])]} />
        </Grid>

        <Sec t="Notes" />
        <textarea value={f.notes} onChange={e=>set('notes',e.target.value)}
          placeholder="Anything else about this caller…"
          style={{ ...s.input, width:'100%', height:80, resize:'vertical' }} />

        <div style={s.actions}>
          <button disabled={busy} onClick={() => {
              const dirty = f.first_name || f.last_name || f.phone || f.notes;
              if (!dirty || window.confirm('Discard this lead? Nothing has been saved.')) onCancel && onCancel();
            }}
            style={{ ...s.btn, background: '#fff', color: '#374151', border: '1px solid #d1d5db' }}>
            Cancel
          </button>
          <button disabled={busy} onClick={() => create(false)} style={s.btn}>
            {busy ? 'Creating…' : 'Create Lead'}
          </button>
          <button disabled={busy} onClick={() => {
              if (!canVob) return alert('Almost there — the VOB still needs:\n\n• ' + vobMissing().join('\n• '));
              create(true);
            }}
            style={{ ...s.btnBlue, opacity: canVob ? 1 : 0.7, cursor: 'pointer' }}
            title={canVob ? '' : 'Click to see what is still needed'}>
            {busy ? 'Working…' : '📋 Create & Request VOB'}
          </button>
        </div>
        {!canVob && (
          <div style={s.hint}>
            VOB needs: name, DOB, payer, member ID
          </div>
        )}
      </div>
    </div>
  );
}

const YN = [['','—'],['true','Yes'],['false','No']];

function PayerCard({ score }) {
  const c = score.recommendation === 'GREEN' ? '#16a34a'
          : score.recommendation === 'YELLOW' ? '#f59e0b' : '#dc2626';
  const m = (n) => n ? '$' + Math.round(n).toLocaleString() : '—';
  return (
    <div style={{ ...s.payerCard, borderLeft:`4px solid ${c}` }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
        <span style={{ background:c, color:'#fff', fontWeight:800, fontSize:12,
                       borderRadius:6, padding:'3px 10px' }}>Tier {score.tier}</span>
        <span style={{ fontWeight:700 }}>{score.payer}</span>
        <span style={s.dim}>· {score.claims_count.toLocaleString()} paid claims</span>
      </div>
      <table style={{ width:'100%', fontSize:13, borderCollapse:'collapse' }}>
        <thead><tr>
          {['LOC','Day Rate','Avg LOS','Episode Value','Collection'].map(h =>
            <th key={h} style={s.th}>{h}</th>)}
        </tr></thead>
        <tbody>
          {score.by_loc.map(r => (
            <tr key={r.loc} style={{ borderTop:'1px solid #f1f5f9' }}>
              <td style={{ ...s.td, fontWeight:700, color:'#1e40af' }}>{r.loc}</td>
              <td style={s.td}>{m(r.avg_day_rate)}</td>
              <td style={s.td}>{r.avg_los ? r.avg_los.toFixed(1) + ' days' : '—'}</td>
              <td style={{ ...s.td, color:'#16a34a', fontWeight:600 }}>{m(r.avg_episode_value)}</td>
              <td style={s.td}>{r.collection_rate ? r.collection_rate.toFixed(1) + '%' : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Sec = ({ t }) => (
  <div style={{ fontSize:11, fontWeight:800, color:'#94a3b8', textTransform:'uppercase',
                letterSpacing:'0.06em', margin:'20px 0 8px', paddingBottom:4,
                borderBottom:'1px solid #f1f5f9' }}>{t}</div>
);

const Grid = ({ children }) => (
  <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px 14px' }}>
    {children}
  </div>
);

const maskDob = (raw) => {
  // Honor typed separators: '8/5/1990' pads to 08/05/1990 instead of garbling to 85/19/90.
  const toks = String(raw || '').split(/[^0-9]+/);
  let mm = toks[0] || '', dd = toks[1] || '', yy = (toks.slice(2).join('') || '');
  if (toks.length > 1 && mm.length === 1) mm = '0' + mm;
  if (toks.length > 2 && dd.length === 1) dd = '0' + dd;
  const d = (mm + dd + yy).replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return d.slice(0,2) + '/' + d.slice(2);
  return d.slice(0,2) + '/' + d.slice(2,4) + '/' + d.slice(4);
};
const dobToISO = (v) => {
  if (!v) return v;
  const m = String(v).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? (m[3] + '-' + m[1] + '-' + m[2]) : v;
};
function SearchSelect({ v, on, opts, ph }) {
  const [open, setOpen] = useState(false);
  const q = String(v || '').toLowerCase();
  const hits = (opts || []).filter(Boolean).filter(o => String(o).toLowerCase().includes(q)).slice(0, 30);
  return (
    <div style={{ position:'relative' }}>
      <input value={v ?? ''} placeholder={ph || 'Type to search…'}
        onChange={e=>{ on(e.target.value); setOpen(true); }}
        onFocus={()=>setOpen(true)}
        onBlur={()=>setTimeout(()=>setOpen(false), 200)}
        style={{ ...s.input, width:'100%' }} />
      {open && hits.length > 0 && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:20,
          background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius:8, maxHeight:220,
          overflowY:'auto', boxShadow: tokens.shadow }}>
          {hits.map(o => (
            <div key={o} onMouseDown={()=>{ on(o); setOpen(false); }}
              style={{ padding:'9px 10px', cursor:'pointer', fontSize:13, borderBottom: `1px solid ${tokens.borderSoft}` }}>
              {o}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function F({ l, v, on, opts, type='text', wide, onAdd, ph, search }) {
  const st = wide ? { gridColumn:'span 2' } : {};
  const listId = search ? 'dl_' + l.replace(/\W/g, '') : null;
  return (
    <div style={st}>
      <div style={s.label}>
        {l}
        {onAdd && <button onClick={onAdd} style={s.addBtn}>+ Add</button>}
      </div>
      {search && opts ? (
        <SearchSelect v={v} on={on} opts={opts} ph={ph} />
      ) : opts ? (
        <select value={v ?? ''} onChange={e=>on(e.target.value)} style={{ ...s.input, width:'100%' }}>
          {opts.map(o => Array.isArray(o)
            ? <option key={o[0]} value={o[0]}>{o[1]}</option>
            : <option key={o} value={o}>{o || '—'}</option>)}
        </select>
      ) : (
        <input type={type} value={v ?? ''} placeholder={ph} onChange={e=>on(e.target.value)}
          style={{ ...s.input, width:'100%' }} />
      )}
    </div>
  );
}

const s = {
  wrap: { padding:24, maxWidth:1100, margin:'0 auto' },
  h1: { fontSize:24, fontWeight:800, color:'#0f172a', margin:0 },
  sub: { color:'#64748b', margin:'4px 0 20px', fontSize:14 },
  card: { background:'#fff', border:'1px solid #e2e8f0', borderRadius:10, padding:20 },
  input: { padding:'7px 9px', borderRadius:6, border:'1px solid #cbd5e1',
           fontSize:13, boxSizing:'border-box' },
  label: { fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase',
           letterSpacing:'0.04em', marginBottom:2 },
  addBtn: { marginLeft:6, background:'none', border:'none', color:'#2563eb',
            fontSize:10, fontWeight:700, cursor:'pointer', padding:0 },
  actions: { display:'flex', gap:10, marginTop:24 },
  btn: { background:'#0f172a', color:'#fff', border:'none', borderRadius:8,
         padding:'12px 24px', fontSize:14, fontWeight:700, cursor:'pointer' },
  btnBlue: { background:'#2563eb', color:'#fff', border:'none', borderRadius:8,
             padding:'12px 24px', fontSize:14, fontWeight:700 },
  hint: { fontSize:12, color:'#94a3b8', marginTop:8 },
  payerCard: { background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8,
               padding:14, marginTop:14 },
  dim: { fontSize:12, color:'#94a3b8' },
  th: { padding:'6px 8px', textAlign:'left', fontSize:10, fontWeight:700,
        color:'#64748b', textTransform:'uppercase' },
  td: { padding:'7px 8px', color:'#334155' },
  empty: { padding:60, textAlign:'center', color:'#94a3b8' },
};